import {useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Grid} from '@mui/material';

import {Loading} from '../../../components/Loading/Loading';
import {CompanyContext} from '../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import JobPostingsFilter from '../../../features/company/recruiting/job-postings/components/JobPostingsFilters/JobPostingsFilter';
import JobPostingsHeader from '../../../features/company/recruiting/job-postings/components/JobPostingsHeader';
import JobPostingsListViewSummary from '../../../features/company/recruiting/job-postings/components/ListView/ListView';
import {NewJobPosting} from '../../../features/company/recruiting/job-postings/components/new/NewJobPosting/NewJobPosting';
import {JobPosting} from '../../../features/company/recruiting/job-postings/types/JobPostingsTypes';
import {CompanyAdminRoleData} from '../../../types/User';

export default function JobPostingsPage() {
  const {jobPostings, departments, locations} = useContext(CompanyContext);
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const [archived, setArchived] = useState<JobPosting[]>([]);
  const [active, setActive] = useState<JobPosting[]>([]);
  const [selected, setSelected] = useState<string | null>('active');
  const [appliedFilters, setAppliedFilters] = useState<any>({
    department: [],
    location: [],
  });

  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();

  const handleToggle = (value: string) => {
    if (value === 'active') {
      setSelected('active');
    } else {
      setSelected('archived');
    }
  };

  const handleFilteredJobPostings = (optionsIds: any) => {
    setAppliedFilters((prev: any) => {
      return {
        ...prev,
        ...optionsIds,
      };
    });
  };

  useEffect(() => {
    if (jobPostings.isLoading) return;

    const filterActiveJobPostings = (jobPostings: JobPosting[]) => {
      const activeJobPostings = jobPostings.filter((jobPosting) => {
        return jobPosting.archivedAt === null;
      });
      setActive(activeJobPostings);
    };

    const filterArchivedJobPostings = (jobPostings: JobPosting[]) => {
      const arhivedJobPostings = jobPostings.filter((jobPosting) => {
        return jobPosting.archivedAt !== null;
      });
      setArchived(arhivedJobPostings);
    };
    if (jobPostings.data) {
      filterActiveJobPostings(jobPostings.data);
      filterArchivedJobPostings(jobPostings.data);
    }
  }, [jobPostings.isLoading, jobPostings.data]);

  const handleNewJobPostingCreated = (newJobPosting: JobPosting) => {
    jobPostings.invalidateCache();
    setActive((prev) => {
      return prev && prev.length > 0 ? [newJobPosting, ...prev] : [newJobPosting];
    });
  };

  const handleUpdateJobPosting = (jobPosting: JobPosting) => {
    if (jobPosting) {
      jobPostings.invalidateCache();
      setActive((prev) => {
        if (prev) {
          const jobPostingIndex = prev.findIndex((jp) => jp.resourceId === jobPosting.resourceId);
          if (jobPostingIndex > -1) {
            const newActive = [...prev];
            newActive[jobPostingIndex] = jobPosting;
            return newActive;
          }
        }
        return prev;
      });
    }
  };

  const handleJobPostingStatusChange = (jobPosting: JobPosting | undefined, action: 'archive' | 'activate') => {
    if (!jobPosting) return;

    if (action === 'archive') {
      const filteredJobs = active?.filter((job) => job.resourceId !== jobPosting.resourceId);
      setActive(filteredJobs);
      setArchived((prev) => [jobPosting, ...prev]);
      dispatchMessage(t('shared.archived'), 'success');
    }

    if (action === 'activate') {
      const filteredArchivedJobs = archived?.filter((job) => job.resourceId !== jobPosting.resourceId);
      setArchived(filteredArchivedJobs);
      setActive((prev) => [jobPosting, ...prev]);
      dispatchMessage(t('shared.activated'), 'success');
    }
  };

  const handleDeleteJobPosting = (jobPosting: JobPosting) => {
    if (jobPosting) {
      jobPostings.invalidateCache();
      setActive((prev) => {
        if (prev) {
          const jobPostingIndex = prev.findIndex((jp) => jp.resourceId === jobPosting.resourceId);
          if (jobPostingIndex > -1) {
            const newActive = [...prev];
            newActive.splice(jobPostingIndex, 1);
            return newActive;
          }
        }
        return prev;
      });
      dispatchMessage(t('shared.deleted'), 'success');
    }
  };

  return (
    <Grid container>
      <JobPostingsHeader active={active} archived={archived} selected={selected} handleToggle={handleToggle} />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Grid item xs={12} sm={7}>
          <JobPostingsFilter
            companyLocations={locations.data || []}
            companyDepartments={departments.data || []}
            handleFilteredJobPostings={handleFilteredJobPostings}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
          md={5}
          sx={{
            display: 'flex',
            justifyContent: {
              sm: 'flex-end',
            },
            height: 'fit-content',
          }}
        >
          <NewJobPosting
            companyId={companyId}
            companyLocations={locations.data || []}
            afterSubmit={handleNewJobPostingCreated}
            handleUpdateJobPosting={handleUpdateJobPosting}
          />
        </Grid>

        {jobPostings.isLoading || locations.isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              minHeight: {xs: '30vh', sm: '40vh', md: '50vh'},
            }}
          >
            <Loading />
          </Box>
        ) : (
          <JobPostingsListViewSummary
            companyId={companyId}
            active={active}
            archived={archived}
            selected={selected}
            appliedFilters={appliedFilters}
            handleUpdateJobPosting={handleUpdateJobPosting}
            handleDeleteJobPosting={handleDeleteJobPosting}
            handleJobPostingStatusChange={handleJobPostingStatusChange}
            companyLocations={locations.data || []}
            setSelected={setSelected}
          />
        )}
      </Grid>
    </Grid>
  );
}
